import { useEffect, useState } from "react";
import closeButton from '../../assets/images/close.png'
import AddNewMemberForm from "../../components/dashboard/Prceedings/Members/AddNewMemberForm";
import ModalComponent from "../../components/Modal/FormModalComponent";
import { createMember, getMembers, updateMember } from "../../services/AppService";
import ViewMemberDetailsModalComponent from "../../components/Modal/ViewMemberDetailsModalComponent";
import { useNavigate } from "react-router-dom";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import { useTranslation } from "react-i18next";

const Members = () => {

    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);

    // Pagination & search states
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [editingMember, setEditingMember] = useState(null);

    const [confirmModal, setConfirmModal] = useState({
        open: false,
        formData: null, // Temporary storage for the payload
    });

    const [messageModal, setMessageModal] = useState({
        open: false,
        type: "success",
        title: "",
        message: "",
    });

    const showSuccess = (msg) => {
        setMessageModal({
            open: true,
            type: "success",
            title: "Success",
            message: msg,
        });
    };

    const showError = (msg) => {
        setMessageModal({
            open: true,
            type: "error",
            title: "Error",
            message: msg,
        });
    };

    const handleFormSubmit = (formData) => {
        setIsModalOpen(false); // 👈 Close the add form modal first
        setConfirmModal({
            open: true,
            formData: formData,
        });
    };

    // If they cancel the confirmation, you might want to re-open the form
    const handleCancelConfirm = () => {
        setConfirmModal({ open: false, formData: null });
        setIsModalOpen(true); //  Re-open form if they changed their mind
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [search]);

    useEffect(() => {
        fetchMembers();
    }, [page, size, debouncedSearch]);

    const fetchMembers = async () => {
        try {
            setLoading(true);
            const pageData = await getMembers(debouncedSearch, page, size);
            setMembers(pageData.content || []);
            setTotalPages(pageData.totalPages || 0);
        } catch (error) {
            console.error("Error fetching members", error);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmSave = async () => {
        const { formData } = confirmModal;
        try {
            let res;
            if (editingMember) {
                res = await updateMember(editingMember.id, formData);
                showSuccess("Member updated successfully");
            } else {
                res = await createMember(formData);
                showSuccess("Member added successfully");
            }

            fetchMembers();
            setConfirmModal({ open: false, formData: null }); // Close confirm
            setEditingMember(null);
        } catch (error) {
            showError(error.response?.data?.message || "Failed to save");
            setIsModalOpen(true); // 👈 Re-open form so user can fix issues
            setConfirmModal({ open: false, formData: null });
        }
    };


    return (
        <div className="bg-white shadow-lg rounded-2xl p-6">
            {/* Header */}
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-xl font-semibold text-blue">
                    {t('pageTitles.member')}
                </h4>
                <img
                    src={closeButton}
                    alt="Close"
                    className="w-6 h-6 cursor-pointer"
                    onClick={() => navigate(-1)}
                />
            </div>

            <div className="text-end mb-2">
                <button className="btnAdd" onClick={() => setIsModalOpen(true)}>
                    {t('common.addNew')}
                </button>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <div className="flex items-center gap-2">
                    <label className="text-sm">{t('common.show')}</label>
                    <select
                        value={size}
                        onChange={(e) => {
                            setSize(Number(e.target.value));
                            setPage(0); // reset to first page
                        }}
                        className="border rounded px-2 py-1 text-sm"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                    <label className="text-sm">{t('common.entries')}</label>
                </div>
                <div>
                    <input
                        type="text"
                        placeholder={t('common.search')}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0); // reset to first page
                        }}
                        className="border rounded px-3 py-1 text-sm"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                {loading ? (
                    <p className="text-center p-4">Loading members...</p>
                ) : (
                    <table className="w-full border text-sm ctable rounded-lg">
                        <thead>
                            <tr className="bg-[#a0adf1] text-white">
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.srNo')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('common.name')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.mobileNo')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('forms.member.wardName')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.edit')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.view')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.status')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center py-4">
                                        No members found
                                    </td>
                                </tr>
                            ) : (
                                members.map((m, idx) => (
                                    <tr
                                        key={m.id}
                                        className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"
                                            } hover:bg-gray-100`}
                                    >
                                        <td className="px-3 py-2 text-center">{page * size + idx + 1}</td>
                                        <td className="px-3 py-2">{m.name}</td>
                                        <td className="px-3 py-2">{m.contactPrimary}</td>
                                        <td className="px-3 py-2">{m.wardName}</td>
                                        <td className="px-3 py-2 text-center">
                                            <button className="px-3 py-1 btnImgEdit rounded text-xs"
                                                onClick={() => {
                                                    setEditingMember(m);
                                                    setIsModalOpen(true);
                                                }}
                                            ></button>
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <button
                                                className="px-3 btnImgView py-1 rounded text-xs"
                                                onClick={() => {
                                                    setSelectedMember(m);
                                                    setViewModalOpen(true);
                                                }}
                                            ></button>
                                        </td>
                                        <td className="px-3 py-2 text-center">{m.active ? "Active" : "Inactive"}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Footer Info + Pagination */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm">
                <p>
                    {t('common.showing')} {page * size + 1} {t('common.to')} {page * size + members.length} {t('common.of')}{" "}
                    {totalPages * size} {t('common.entries')}
                </p>
                <div className="flex gap-2 mt-2 sm:mt-0">
                    <button
                        className="px-3 py-1 border rounded disabled:opacity-50"
                        onClick={() => setPage((p) => Math.max(p - 1, 0))}
                        disabled={page === 0}
                    >
                        {t('common.previous')}
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setPage(i)}
                            className={`px-3 py-1 border rounded ${page === i
                                ? "bg-gradient-to-b from-white to-[#dcdcdc] border border-[#979797]"
                                : ""
                                }`}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button
                        className="px-3 py-1 border rounded disabled:opacity-50"
                        onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
                        disabled={page === totalPages - 1}
                    >
                        {t('common.next')}
                    </button>
                </div>
            </div>

            {/* FORM MODAL */}
            <AddNewMemberForm
                open={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingMember(null); }}
                onSubmit={handleFormSubmit}
                editData={editingMember}
            />


            <ConfirmActionModal
                open={confirmModal.open}
                title={editingMember ? "Confirm Update" : "Confirm Addition"}
                message={
                    editingMember
                        ? "Are you sure you want to update this member's details?"
                        : "Are you sure you want to add this new member to the system?"
                }
                confirmText={editingMember ? "UPDATE" : "SAVE"}
                onConfirm={handleConfirmSave}
                onClose={handleCancelConfirm}
            />

            {/* FEEDBACK MODAL */}
            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                type={messageModal.type}
                title={messageModal.title}
                message={messageModal.message}
            />

            {/* View Modal */}
            <ViewMemberDetailsModalComponent
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                member={selectedMember}
            />

        </div>
    )
}

export default Members;